/**
 * Sunday Clovr — favourites.
 *
 * Persistence is localStorage, deliberately. Customer-account-backed
 * favourites would survive across devices, but on Shopify that needs
 * either a metafield write from an app or a third-party wishlist app —
 * a paid dependency and an external data processor. localStorage keeps
 * the data on the shopper's device and adds no vendor. The trade-off is
 * that favourites do not follow a shopper to another browser.
 */
const KEY = 'sc:favourites:v1';

// Set when the saved list cannot be read: storage access failed (private mode,
// blocked site data) or the stored value is not a valid list (corrupt JSON or a
// non-array). While true nothing is written: an empty read must never overwrite
// a value that may still hold the shopper's items.
let readFailed = false;
// Set when the last write failed (quota, blocked storage); cleared by a successful write.
let writeFailed = false;

const read = () => {
  let raw = null;
  try {
    raw = localStorage.getItem(KEY);
  } catch {
    readFailed = true;
    return [];
  }
  if (raw === null || raw === '') { readFailed = false; return []; }
  try {
    const val = JSON.parse(raw);
    if (!Array.isArray(val)) throw new TypeError('favourites: stored value is not a list');
    readFailed = false;
    return val.filter((h) => typeof h === 'string');
  } catch {
    readFailed = true;         // corrupt value: left in place, never overwritten
    return [];
  }
};

// Returns true only when the list was actually persisted.
const write = (list) => {
  if (readFailed) return false;
  try {
    localStorage.setItem(KEY, JSON.stringify([...new Set(list)]));
    writeFailed = false;
    return true;
  } catch {
    writeFailed = true;
    return false;
  }
};

const emit = () => {
  document.dispatchEvent(new CustomEvent('sc:favourites:change', { detail: { items: read() } }));
};

const PROBLEMS = {
  read: 'Wishlist is unavailable: saved items could not be read in this browser.',
  write: 'Wishlist could not be saved in this browser (storage blocked or full).',
};

export const Favourites = {
  all: read,
  has: (handle) => read().includes(handle),
  // '' when saving works; 'read' or 'write' names the failure (message() puts it into words).
  problem() { read(); return readFailed ? 'read' : writeFailed ? 'write' : ''; },
  message() { return PROBLEMS[this.problem()] || ''; },
  available() { return this.problem() === ''; },
  // Returns the persisted state of `handle` after the call (unchanged if the write failed).
  toggle(handle) {
    if (!handle) return false;
    const list = read();
    const wasOn = list.includes(handle);
    const next = wasOn ? list.filter((h) => h !== handle) : [...list, handle];
    const saved = write(next);
    if (saved) emit();                  // listeners re-render only after a real change
    return saved ? !wasOn : wasOn;
  },
  // Returns true when the removal was persisted.
  remove(handle) {
    const saved = write(read().filter((h) => h !== handle));
    if (saved) emit();
    return saved;
  },
};

/* --- status line (wishlist page) ------------------------------- */
// Page-level status lines (the wishlist page) take precedence; otherwise the global
// notice rendered by layout/theme.liquid shows the message under the page.
const announce = (msg) => {
  const local = document.querySelectorAll('[data-sc-fav-status]');
  const targets = local.length ? local : document.querySelectorAll('[data-sc-fav-status-global]');
  targets.forEach((el) => { el.textContent = msg; });
};

/* --- count badge in the header --------------------------------- */
const paintCount = () => {
  const n = read().length;
  document.querySelectorAll('[data-sc-fav-count]').forEach((el) => {
    el.textContent = n > 0 ? String(n) : '';
    el.hidden = n === 0;
  });
  document.querySelectorAll('[data-sc-fav-link]').forEach((el) => {
    el.setAttribute('aria-label', n === 1 ? 'Wishlist, 1 item' : `Wishlist, ${n} items`);
  });
};

/* --- toggle buttons -------------------------------------------- */
const paintButton = (btn) => {
  const on = Favourites.has(btn.dataset.scFav);
  btn.setAttribute('aria-pressed', String(on));
  btn.classList.toggle('is-on', on);
  const label = on ? 'Remove from wishlist' : 'Add to wishlist';
  btn.setAttribute('aria-label', label);
  const sr = btn.querySelector('[data-sc-fav-label]');
  if (sr) sr.textContent = label;
};

const paintAll = () => {
  document.querySelectorAll('[data-sc-fav]').forEach(paintButton);
  paintCount();
};

document.addEventListener('click', (e) => {
  const btn = e.target.closest?.('[data-sc-fav]');
  if (!btn) return;
  e.preventDefault();
  Favourites.toggle(btn.dataset.scFav);
  paintButton(btn);             // reflects the persisted state, not the intended one
  const msg = Favourites.message();
  if (msg) {
    // Say so where it can be perceived: a tooltip on the heart and a persistent
    // role="status" line (the wishlist page's own, or the global notice).
    btn.setAttribute('title', msg);
    announce(msg);
  } else {
    btn.removeAttribute('title');
    announce('');
  }
});

document.addEventListener('sc:favourites:change', paintAll);
// Another tab changed (or cleared) the saved list: re-render everything, not only the hearts.
addEventListener('storage', (e) => { if (e.key === KEY || e.key === null) emit(); });
document.addEventListener('DOMContentLoaded', paintAll);
paintAll();

window.SCFavourites = Favourites;
